import React, { useState, useEffect } from "react";

/* ============================================================================
   ELVIS ALOHA ARCHIVE — ARTIFACT-LED COLLECTION SHOWCASE
   
   Museum-quality presentation built around the actual collection.
   Properly categorized: PSA proofs, admission tickets, stubs, mockups, production materials.
   No sales/auction language. Historically qualified claims only.
   ============================================================================ */

// PSA Population Data (captured July 2025 - figures may change)
const PSA_DATA = {
  captureDate: "July 2025",
  proofs: {
    total: 50,
    description: "Aloha From Hawaii/H.I.C. Arena, Jan.14, Honolulu, Hi-Not Valid for Admission",
    grades: { 6: 2, 7: 5, 8: 15, 9: 25, 10: 3 }
  },
  stubs: {
    total: 12,
    description: "Aloha From Hawaii/H.I.C. Arena, Jan.14, Honolulu, Hi",
    grades: { AUTH: 4, 1: 1, 3: 1, 4: 2, 8: 1 }
  },
  full: {
    total: 4,
    description: "H.I.C. Arena, Honolulu/Jan.14, Proof-Not Valid for Admission",
    grades: { 6: 1, 7: 2, 8: 1 }
  }
};

// Collection imagery paths
const IMAGES = {
  // Existing collection photos
  heroCollection: "/assets/collection/real-graceland-costume-tickets-2.jpg",
  globePortrait: "/assets/collection/real-proof-globe-portrait-1.jpg",
  documents: "/assets/collection/real-graceland-documents-1.jpg",
  artworkProofs: "/assets/collection/real-proof-artwork-grid.jpg",
  ticketDetail: "/assets/collection/real-graceland-costume-tickets-1.jpg",
  gracelandFramed: "/assets/collection/real-graceland-framed-tickets-1.jpg",
  // New archive assets
  gracelandWide: "/assets/archive/graceland_display_wide.webp",
  collectionDisplay1: "/assets/archive/collection_display_1.png",
  collectionDisplay2: "/assets/archive/collection_display_2.png",
  collectionDisplay3: "/assets/archive/collection_display_3.png",
  collectionDisplay4: "/assets/archive/collection_display_4.png",
  collectionDisplay5: "/assets/archive/collection_display_5.png",
  collectionDisplay6: "/assets/archive/collection_display_6.png"
};

/* ============================================================================
   SMOOTH SCROLL
   ============================================================================ */
const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    const headerOffset = 80;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  }
};

/* ============================================================================
   NAVIGATION
   ============================================================================ */
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const navItems = [
    { id: 'inventory', label: 'Inventory' },
    { id: 'psa-proofs', label: 'PSA Proofs' },
    { id: 'production', label: 'Production' },
    { id: 'graceland', label: 'Graceland' },
    { id: 'provenance', label: 'Provenance' },
    { id: 'about', label: 'About' }
  ];

  return (
    <>
      <style>{`
        .archive-nav {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 100;
          transition: all 0.3s ease;
        }
        .archive-nav.scrolled {
          background: rgba(255, 255, 255, 0.98);
          box-shadow: 0 1px 0 rgba(0,0,0,0.08);
        }
        .archive-nav:not(.scrolled) {
          background: transparent;
        }
        .archive-nav-link {
          padding: 10px 16px;
          font-family: 'Source Sans 3', sans-serif;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          background: none;
          border: none;
          cursor: pointer;
          transition: color 0.2s;
        }
        .archive-nav-link.light { color: rgba(255,255,255,0.85); }
        .archive-nav-link.light:hover { color: #C9A962; }
        .archive-nav-link.dark { color: #333; }
        .archive-nav-link.dark:hover { color: #8B7355; }
        @media (max-width: 900px) {
          .archive-desktop-nav { display: none !important; }
          .archive-mobile-toggle { display: flex !important; }
        }
        @media (min-width: 901px) {
          .archive-desktop-nav { display: flex !important; }
          .archive-mobile-toggle { display: none !important; }
        }
      `}</style>

      <nav className={`archive-nav ${scrolled ? 'scrolled' : ''}`} data-testid="archive-nav">
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '20px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
             style={{ textDecoration: 'none' }}>
            <span style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: '18px',
              fontWeight: 600,
              color: scrolled ? '#1a1a1a' : 'white',
              letterSpacing: '0.06em'
            }}>
              ELVIS ALOHA ARCHIVE
            </span>
          </a>

          <div className="archive-desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {navItems.map(item => (
              <button key={item.id} 
                      className={`archive-nav-link ${scrolled ? 'dark' : 'light'}`} 
                      onClick={() => scrollToSection(item.id)}>
                {item.label}
              </button>
            ))}
          </div>

          <button className="archive-mobile-toggle" onClick={() => setMobileOpen(!mobileOpen)}
            style={{ display: 'none', alignItems: 'center', justifyContent: 'center', width: '48px', height: '48px', 
                     background: 'none', border: 'none', cursor: 'pointer' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={scrolled ? '#1a1a1a' : 'white'} strokeWidth="1.5">
              {mobileOpen ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
                         : <><line x1="4" y1="7" x2="20" y2="7"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="17" x2="20" y2="17"/></>}
            </svg>
          </button>
        </div>

        {/* Mobile menu overlay */}
        {mobileOpen && (
          <div style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            background: 'white', 
            zIndex: 200,
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ padding: '20px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee' }}>
              <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', fontWeight: 600, color: '#1a1a1a' }}>
                ELVIS ALOHA ARCHIVE
              </span>
              <button onClick={() => setMobileOpen(false)} style={{ background: 'none', border: 'none', padding: '12px', cursor: 'pointer' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="1.5">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div style={{ padding: '24px 32px', flex: 1 }}>
              {navItems.map(item => (
                <button key={item.id} onClick={() => { scrollToSection(item.id); setMobileOpen(false); }}
                  style={{ display: 'block', width: '100%', padding: '18px 0', textAlign: 'left', background: 'none', border: 'none',
                    fontFamily: "'Source Sans 3', sans-serif", fontSize: '16px', fontWeight: 600, letterSpacing: '0.06em',
                    textTransform: 'uppercase', color: '#1a1a1a', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}>
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

/* ============================================================================
   SECTION: HERO
   ============================================================================ */
const HeroSection = () => (
  <section id="top" style={{
    position: 'relative',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden'
  }} data-testid="hero-section">
    <div style={{
      position: 'absolute',
      inset: 0,
      backgroundImage: `url(${IMAGES.heroCollection})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      filter: 'brightness(1.8)'
    }} />

    <div style={{
      position: 'relative',
      width: '100%',
      maxWidth: '1000px',
      margin: '0 auto',
      padding: '140px 32px 100px',
      textAlign: 'center'
    }}>
      <p style={{
        marginBottom: '20px',
        fontFamily: "'Source Sans 3', sans-serif",
        fontSize: '13px',
        fontWeight: 600,
        letterSpacing: '0.25em',
        textTransform: 'uppercase',
        color: '#C9A962'
      }}>
        A Documented Historical Archive
      </p>

      <h1 style={{
        margin: '0 0 28px',
        fontFamily: "'Cormorant Garamond', serif",
        fontSize: 'clamp(32px, 6vw, 58px)',
        fontWeight: 400,
        lineHeight: 1.15,
        color: 'white',
        letterSpacing: '-0.01em'
      }}>
        The Elvis Presley<br />
        <em>Aloha from Hawaii</em><br />
        Ticket &amp; Production Archive
      </h1>

      <p style={{
        maxWidth: '720px',
        margin: '0 auto 40px',
        fontFamily: "'Source Serif 4', serif",
        fontSize: 'clamp(16px, 2.2vw, 19px)',
        lineHeight: 1.75,
        color: 'rgba(255,255,255,0.8)'
      }}>
        Original design mockups, PSA-certified ticket proofs, admission artifacts, production materials, 
        and documentation connected to Elvis Presley's January 1973 benefit concert at the 
        Honolulu International Center Arena.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '48px', marginBottom: '8px' }}>
        {[
          { value: 'PSA-Certified', label: 'Proof Archive' },
          { value: 'Production', label: 'Mockups & Layouts' },
          { value: 'Sept 14, 2022', label: 'Graceland Documented' }
        ].map((stat, i) => (
          <div key={i} style={{ textAlign: 'center' }}>
            <p style={{ margin: 0, fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', fontWeight: 500, color: '#C9A962' }}>
              {stat.value}
            </p>
            <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 600, 
                        letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
              {stat.label}
            </p>
          </div>
        ))}
      </div>
    </div>

    <button onClick={() => scrollToSection('inventory')} style={{
      position: 'absolute', bottom: '32px', left: '50%', transform: 'translateX(-50%)',
      background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', padding: '12px'
    }} aria-label="Scroll to content">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="m6 9 6 6 6-6"/>
      </svg>
    </button>
  </section>
);

/* ============================================================================
   SECTION: COLLECTION INVENTORY
   ============================================================================ */
const InventorySection = () => (
  <section id="inventory" style={{ padding: '100px 32px', background: 'white' }} data-testid="inventory-section">
    <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '56px' }}>
        <p style={{ marginBottom: '14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', fontWeight: 700, 
                    letterSpacing: '0.18em', textTransform: 'uppercase', color: '#8B7355' }}>
          Collection Inventory
        </p>
        <h2 style={{ margin: '0 0 20px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px, 4.5vw, 42px)', 
                     fontWeight: 400, color: '#1a1a1a', lineHeight: 1.2 }}>
          What the Archive Contains
        </h2>
        <p style={{ maxWidth: '700px', margin: '0 auto', fontFamily: "'Source Sans 3', sans-serif", fontSize: '17px', 
                    lineHeight: 1.75, color: '#555' }}>
          The archive preserves several connected categories of material from the Aloha ticket production. 
          Understanding these categories is essential—<strong>not every item is an "original ticket."</strong>
        </p>
      </div>

      {/* Category Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '48px' }}>
        {[
          { 
            category: 'PSA-Certified Proofs',
            status: '50 Population',
            desc: 'Ticket proofs marked "Not Valid for Admission." These are production examples, not event-admission tickets.',
            highlight: true
          },
          { 
            category: 'Used Ticket Stubs',
            status: '12 Population',
            desc: 'Detached stubs from tickets used at the January 14, 1973 concert, showing original tear lines.'
          },
          { 
            category: 'Full Admission Examples',
            status: '4 Population',
            desc: 'Complete tickets that may have been valid for admission, distinguished from production proofs.'
          },
          { 
            category: 'Original Mockups',
            status: 'Archive',
            desc: 'Prototype ticket designs with handwritten annotations showing the design evolution process.'
          },
          { 
            category: 'Signed Production Layouts',
            status: 'Archive',
            desc: 'Final layout plans signed by Fred Haines of Globe Tickets, dated November 21, 1972.'
          },
          { 
            category: 'Seating Zone Plans',
            status: 'Archive',
            desc: 'Original planning documents showing HIC Arena, Loge, and Upper Level ticket zones and quantities.'
          },
          { 
            category: 'Printing Negatives',
            status: 'Archive',
            desc: 'Original negative slides used by Globe Tickets to print the Aloha tickets.'
          },
          { 
            category: 'Historical Documentation',
            status: 'Archive',
            desc: 'Correspondence, newspaper coverage, photographs, and provenance records from 1972-1973.'
          }
        ].map((item, idx) => (
          <div key={idx} style={{ 
            padding: '24px', 
            background: item.highlight ? '#f8f6f3' : '#fafafa', 
            border: `1px solid ${item.highlight ? '#d4c9b8' : '#e8e8e8'}`,
            borderLeft: item.highlight ? '4px solid #8B7355' : '1px solid #e8e8e8'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
              <h3 style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '16px', fontWeight: 700, color: '#1a1a1a' }}>
                {item.category}
              </h3>
              <span style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 600, 
                             letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8B7355' }}>
                {item.status}
              </span>
            </div>
            <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', lineHeight: 1.65, color: '#666' }}>
              {item.desc}
            </p>
          </div>
        ))}
      </div>

      {/* Important distinction callout */}
      <div style={{ padding: '28px 32px', background: '#1a1a1a', color: 'white' }}>
        <h4 style={{ margin: '0 0 12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', fontWeight: 700, 
                     letterSpacing: '0.1em', textTransform: 'uppercase', color: '#C9A962' }}>
          Important Distinction
        </h4>
        <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '16px', lineHeight: 1.7, color: 'rgba(255,255,255,0.85)' }}>
          PSA-certified "proofs" are explicitly marked <strong>"Not Valid for Admission"</strong> and represent production 
          examples rather than tickets that could have been used to enter the concert. The archive carefully separates 
          these from actual admission tickets and used stubs, which have different PSA population categories.
        </p>
      </div>
    </div>
  </section>
);

/* ============================================================================
   SECTION: PSA-CERTIFIED PROOF ARCHIVE
   ============================================================================ */
const PSAProofsSection = () => (
  <section id="psa-proofs" style={{ padding: '100px 32px', background: '#f8f7f5' }} data-testid="psa-proofs-section">
    <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '56px' }}>
        <p style={{ marginBottom: '14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', fontWeight: 700, 
                    letterSpacing: '0.18em', textTransform: 'uppercase', color: '#8B7355' }}>
          Collection Centerpiece
        </p>
        <h2 style={{ margin: '0 0 20px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px, 4.5vw, 42px)', 
                     fontWeight: 400, color: '#1a1a1a', lineHeight: 1.2 }}>
          The PSA-Certified Proof Archive
        </h2>
        <p style={{ maxWidth: '700px', margin: '0 auto', fontFamily: "'Source Sans 3', sans-serif", fontSize: '17px', 
                    lineHeight: 1.75, color: '#555' }}>
          A substantial group of professionally graded ticket proofs forms the core of the certified collection. 
          These proofs are marked "Not Valid for Admission" and represent production examples from Globe Tickets.
        </p>
      </div>

      {/* Images and Population Data side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 450px), 1fr))', gap: '48px', marginBottom: '48px' }}>
        {/* Image */}
        <div>
          <img src={IMAGES.heroCollection} alt="PSA-certified Aloha from Hawaii ticket proofs"
            style={{ width: '100%', height: 'auto', display: 'block', marginBottom: '12px' }} />
          <p style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', color: '#888', lineHeight: 1.5 }}>
            Representative group of PSA-encapsulated ticket proofs from the archive, documented at Graceland.
          </p>
        </div>

        {/* Population Table */}
        <div>
          <h3 style={{ margin: '0 0 20px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '15px', fontWeight: 700, 
                       letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B7355' }}>
            PSA Population Report
          </h3>
          
          {/* Proofs Table */}
          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ margin: '0 0 12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', fontWeight: 700, color: '#1a1a1a' }}>
              PROOF — Not Valid for Admission
            </h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px' }}>
              <thead>
                <tr style={{ background: '#e8e6e2' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Grade</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>6</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>7</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>8</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>9</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>10</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ background: 'white' }}>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e8e6e2' }}>Count</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e8e6e2' }}>2</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e8e6e2' }}>5</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e8e6e2' }}>15</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e8e6e2' }}>25</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e8e6e2' }}>3</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, borderBottom: '1px solid #e8e6e2', color: '#8B7355' }}>50</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Stubs Table */}
          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ margin: '0 0 12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', fontWeight: 700, color: '#1a1a1a' }}>
              STUB — Used Ticket Stubs
            </h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px' }}>
              <thead>
                <tr style={{ background: '#e8e6e2' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Grade</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>AUTH</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>1</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>3</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>4</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>8</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ background: 'white' }}>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e8e6e2' }}>Count</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e8e6e2' }}>4</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e8e6e2' }}>1</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e8e6e2' }}>1</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e8e6e2' }}>2</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e8e6e2' }}>1</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, borderBottom: '1px solid #e8e6e2', color: '#8B7355' }}>9</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', color: '#888', lineHeight: 1.5, fontStyle: 'italic' }}>
            Population data captured as of {PSA_DATA.captureDate}. PSA population figures may change as additional items are graded.
            The archive does not claim to own the entire PSA population.
          </p>
        </div>
      </div>

      {/* Proof Characteristics */}
      <div style={{ padding: '32px', background: 'white', border: '1px solid #e0ddd8' }}>
        <h3 style={{ margin: '0 0 20px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '15px', fontWeight: 700, 
                     letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B7355' }}>
          Proof Characteristics
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '24px' }}>
          {[
            { label: 'Designation', value: '"Not Valid for Admission"' },
            { label: 'Event', value: 'Aloha From Hawaii/H.I.C. Arena' },
            { label: 'Date', value: 'January 14, 1973' },
            { label: 'Location', value: 'Honolulu, Hawaii' },
            { label: 'Producer', value: 'Globe Tickets, Los Angeles' },
            { label: 'Purpose', value: 'Production/approval proofs' }
          ].map((item, idx) => (
            <div key={idx}>
              <p style={{ margin: '0 0 4px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', fontWeight: 600, 
                          letterSpacing: '0.08em', textTransform: 'uppercase', color: '#888' }}>
                {item.label}
              </p>
              <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '15px', fontWeight: 600, color: '#1a1a1a' }}>
                {item.value}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================================
   SECTION: PRODUCTION HISTORY
   ============================================================================ */
const ProductionSection = () => (
  <section id="production" style={{ padding: '100px 32px', background: 'white' }} data-testid="production-section">
    <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '56px' }}>
        <p style={{ marginBottom: '14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', fontWeight: 700, 
                    letterSpacing: '0.18em', textTransform: 'uppercase', color: '#8B7355' }}>
          Production Lineage
        </p>
        <h2 style={{ margin: '0 0 20px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px, 4.5vw, 42px)', 
                     fontWeight: 400, color: '#1a1a1a', lineHeight: 1.2 }}>
          From Concept to Finished Ticket
        </h2>
        <p style={{ maxWidth: '700px', margin: '0 auto', fontFamily: "'Source Sans 3', sans-serif", fontSize: '17px', 
                    lineHeight: 1.75, color: '#555' }}>
          The archive preserves materials showing how these historic tickets were created—from initial sketches 
          through signed approvals to printing negatives and finished proofs.
        </p>
      </div>

      {/* Production Timeline */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '32px', marginBottom: '48px' }}>
        {[
          {
            stage: '1. Prototype Design',
            date: '1972',
            desc: 'Initial ticket mockups with handwritten annotations showing design decisions and required changes.',
            detail: 'These prototypes document the evolution from concept to approved design.'
          },
          {
            stage: '2. Seating Zone Planning',
            date: '1972',
            desc: 'Original layout documents showing HIC Arena, Loge, and Upper Level zones with ticket quantities.',
            detail: 'Planning documents establish the production scope and ticket distribution.'
          },
          {
            stage: '3. Signed Approval',
            date: 'November 21, 1972',
            desc: 'Final layout plan signed by Fred Haines of Globe Tickets, Los Angeles office.',
            detail: 'The signature and date establish the production timeline and chain of responsibility.'
          },
          {
            stage: '4. Printing Production',
            date: '1972-1973',
            desc: 'Original printing negatives used by Globe Tickets to produce the final tickets.',
            detail: 'The negatives are primary production artifacts rarely preserved after printing runs.'
          },
          {
            stage: '5. Proof Examples',
            date: 'January 1973',
            desc: 'Production proofs marked "Not Valid for Admission" for approval and records.',
            detail: 'These proofs connect directly to the planning documents and negatives.'
          },
          {
            stage: '6. Event Admission',
            date: 'January 14, 1973',
            desc: 'Tickets used for entry to the benefit concert at Honolulu International Center Arena.',
            detail: 'Used stubs with original tear lines document actual event attendance.'
          }
        ].map((item, idx) => (
          <div key={idx} style={{ padding: '28px', background: '#fafaf8', border: '1px solid #e8e6e2' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '16px', fontWeight: 700, color: '#1a1a1a' }}>
                {item.stage}
              </h3>
              <span style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', fontWeight: 600, color: '#8B7355' }}>
                {item.date}
              </span>
            </div>
            <p style={{ margin: '0 0 12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '15px', lineHeight: 1.65, color: '#444' }}>
              {item.desc}
            </p>
            <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', lineHeight: 1.55, color: '#888', fontStyle: 'italic' }}>
              {item.detail}
            </p>
          </div>
        ))}
      </div>

      {/* Key artifact highlight */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))', gap: '48px', alignItems: 'center' }}>
        <div style={{ position: 'relative' }}>
          <img src={IMAGES.artworkProofs} alt="Original Globe Tickets production artwork and proofs"
            style={{ width: '100%', height: 'auto', display: 'block' }} />
          <p style={{ marginTop: '12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', color: '#888', lineHeight: 1.5 }}>
            Production materials including color proofs and printing tests from Globe Tickets.
          </p>
        </div>
        <div>
          <h3 style={{ margin: '0 0 16px', fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', fontWeight: 400, color: '#1a1a1a' }}>
            The Fred Haines Signature
          </h3>
          <p style={{ margin: '0 0 16px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '16px', lineHeight: 1.75, color: '#444' }}>
            The final layout plan bears the signature of <strong>Fred Haines</strong> of Globe Tickets' Los Angeles 
            office, dated <strong>November 21, 1972</strong>. This signature establishes the production approval 
            and timeline.
          </p>
          <p style={{ margin: '0 0 16px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '16px', lineHeight: 1.75, color: '#444' }}>
            In June 2021, Bob Smith, General Manager of Globe Ticket, provided documentation of Fred Haines 
            from their company newsletter, confirming his role in the Compton Division.
          </p>
          <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', lineHeight: 1.6, color: '#888', fontStyle: 'italic' }}>
            Bob Smith has since passed away. Attempts were made to reach out to the family for additional confirmation.
          </p>
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================================
   SECTION: GRACELAND DOCUMENTATION
   ============================================================================ */
const GracelandSection = () => (
  <section id="graceland" style={{ padding: '100px 32px', background: '#0a0a0a', color: 'white' }} data-testid="graceland-section">
    <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '56px' }}>
        <p style={{ marginBottom: '14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', fontWeight: 700, 
                    letterSpacing: '0.18em', textTransform: 'uppercase', color: '#C9A962' }}>
          Graceland Documentation
        </p>
        <h2 style={{ margin: '0 0 20px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px, 4.5vw, 42px)', 
                     fontWeight: 400, lineHeight: 1.2 }}>
          Documented at Graceland
        </h2>
        <p style={{ maxWidth: '700px', margin: '0 auto', fontFamily: "'Source Sans 3', sans-serif", fontSize: '17px', 
                    lineHeight: 1.75, color: 'rgba(255,255,255,0.8)' }}>
          On <strong>September 14, 2022</strong>, the collection was photographed and documented in connection 
          with the Aloha display at Graceland.
        </p>
      </div>

      {/* Gallery Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginBottom: '48px' }}>
        <div>
          <img src={IMAGES.gracelandWide} alt="Graceland Aloha display with jumpsuit collection"
            style={{ width: '100%', height: 'auto', display: 'block', marginBottom: '12px' }} />
          <p style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>
            The Graceland exhibition space featuring the Aloha jumpsuit display where collection materials were documented.
          </p>
        </div>
        <div>
          <img src={IMAGES.heroCollection} alt="Complete ticket archive displayed at Graceland"
            style={{ width: '100%', height: 'auto', display: 'block', marginBottom: '12px' }} />
          <p style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>
            The ticket archive documented at Graceland, showing the scope of preserved materials.
          </p>
        </div>
      </div>

      {/* Documentation Statement */}
      <div style={{ padding: '32px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
        <h3 style={{ margin: '0 0 16px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '15px', fontWeight: 700, 
                     letterSpacing: '0.1em', textTransform: 'uppercase', color: '#C9A962' }}>
          Documentation Statement
        </h3>
        <p style={{ margin: '0 0 16px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '16px', lineHeight: 1.75, color: 'rgba(255,255,255,0.85)' }}>
          The collection was photographed and documented in connection with the Aloha display at Graceland on 
          <strong> September 14, 2022</strong>. This establishes the archive's presence at the most significant 
          Elvis Presley historical location.
        </p>
        <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', lineHeight: 1.6, color: 'rgba(255,255,255,0.6)', fontStyle: 'italic' }}>
          Note: This documentation does not constitute formal authentication by Graceland or Elvis Presley 
          Enterprises. The photographs establish the collection's presence at Graceland on the documented date.
        </p>
      </div>
    </div>
  </section>
);

/* ============================================================================
   SECTION: HISTORICAL CONTEXT
   ============================================================================ */
const HistoricalSection = () => (
  <section style={{ padding: '100px 32px', background: 'white' }}>
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '56px' }}>
        <p style={{ marginBottom: '14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', fontWeight: 700, 
                    letterSpacing: '0.18em', textTransform: 'uppercase', color: '#8B7355' }}>
          Historical Context
        </p>
        <h2 style={{ margin: '0 0 20px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px, 4.5vw, 42px)', 
                     fontWeight: 400, color: '#1a1a1a', lineHeight: 1.2 }}>
          The Aloha from Hawaii Event
        </h2>
      </div>

      <div style={{ fontFamily: "'Source Serif 4', serif", fontSize: '17px', lineHeight: 1.85, color: '#444' }}>
        <p style={{ marginBottom: '20px' }}>
          On January 14, 1973, Elvis Presley performed at the Honolulu International Center Arena in a benefit 
          concert for the Kui Lee Cancer Fund. The concert was transmitted via satellite to portions of Asia 
          and Oceania, including Japan, South Korea, the Philippines, Australia, and New Zealand.
        </p>
        <p style={{ marginBottom: '20px' }}>
          The United States broadcast followed on April 4, 1973, on NBC. International distribution varied by 
          territory and date based on local broadcasting arrangements.
        </p>
        <p style={{ marginBottom: '20px' }}>
          On February 4, 1973, Elvis presented a check for $77,000 to the Kui Lee Cancer Fund. The concert 
          ultimately raised over $300,000 for various Hawaiian charities.
        </p>
        <p>
          The tickets were produced by Globe Tickets of Los Angeles, with the distinctive globe-and-portrait 
          design created under the direction of Artie Samuel, the HIC Box Office Manager who conceptualized 
          the souvenir-quality ticket design.
        </p>
      </div>

      {/* Historical Connection: Jack Lord */}
      <div style={{ marginTop: '48px', padding: '32px', background: '#fafaf8', border: '1px solid #e8e6e2' }}>
        <h3 style={{ margin: '0 0 16px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '15px', fontWeight: 700, 
                     letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B7355' }}>
          Historical Connection: Jack Lord
        </h3>
        <p style={{ margin: '0 0 12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '16px', lineHeight: 1.75, color: '#444' }}>
          Jack Lord of <em>Hawaii Five-O</em> attended the January 14, 1973 concert. According to documented accounts, 
          he was seated in the BB section near Linda Thompson. Jack Lord later donated his ticket to Graceland.
        </p>
        <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', lineHeight: 1.6, color: '#888', fontStyle: 'italic' }}>
          The seating information was handwritten on a blank ticket. This represents a family account and 
          handwritten attribution rather than independently verified documentation.
        </p>
      </div>
    </div>
  </section>
);

/* ============================================================================
   SECTION: PROVENANCE
   ============================================================================ */
const ProvenanceSection = () => (
  <section id="provenance" style={{ padding: '100px 32px', background: '#f8f7f5' }} data-testid="provenance-section">
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '56px' }}>
        <p style={{ marginBottom: '14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', fontWeight: 700, 
                    letterSpacing: '0.18em', textTransform: 'uppercase', color: '#8B7355' }}>
          Documentation
        </p>
        <h2 style={{ margin: '0 0 20px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px, 4.5vw, 42px)', 
                     fontWeight: 400, color: '#1a1a1a', lineHeight: 1.2 }}>
          Provenance &amp; Research
        </h2>
      </div>

      {/* Provenance Levels */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '48px' }}>
        {[
          { 
            level: 'Production Attribution',
            desc: 'Materials attributed to Globe Tickets based on design characteristics, signatures, and physical examination.'
          },
          { 
            level: 'Known Prior Custody',
            desc: 'Documentation of ownership history where records exist, including acquisition from Globe Tickets archives.'
          },
          { 
            level: 'PSA Certification',
            desc: 'Professional grading and encapsulation by PSA with certification numbers and grades.'
          },
          { 
            level: 'Physical Examination',
            desc: 'Direct examination of materials for authenticity markers, printing characteristics, and condition.'
          },
          { 
            level: 'Graceland Documentation',
            desc: 'Photographic documentation at Graceland on September 14, 2022.'
          },
          { 
            level: 'Research Verification',
            desc: 'Cross-referencing with contemporaneous newspaper coverage and historical records from 1972-1973.'
          }
        ].map((item, idx) => (
          <div key={idx} style={{ padding: '24px', background: 'white', border: '1px solid #e0ddd8' }}>
            <h4 style={{ margin: '0 0 8px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '15px', fontWeight: 700, color: '#1a1a1a' }}>
              {item.level}
            </h4>
            <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', lineHeight: 1.65, color: '#666' }}>
              {item.desc}
            </p>
          </div>
        ))}
      </div>

      {/* Unresolved Questions */}
      <div style={{ padding: '32px', background: 'white', border: '1px solid #e0ddd8' }}>
        <h3 style={{ margin: '0 0 16px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '15px', fontWeight: 700, 
                     letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B7355' }}>
          Unresolved Research Questions
        </h3>
        <p style={{ margin: '0 0 16px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '15px', lineHeight: 1.7, color: '#555' }}>
          The archive acknowledges areas where evidence remains incomplete:
        </p>
        <ul style={{ margin: 0, paddingLeft: '20px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '15px', lineHeight: 1.8, color: '#666' }}>
          <li style={{ marginBottom: '8px' }}>Exact total print run quantities for each ticket price point</li>
          <li style={{ marginBottom: '8px' }}>Complete chain of custody for certain production materials prior to documented acquisition</li>
          <li style={{ marginBottom: '8px' }}>Identification of all Globe Tickets personnel involved in the production</li>
          <li style={{ marginBottom: '8px' }}>Precise scope of the archive's holdings versus total PSA population</li>
          <li>Independent verification of certain family accounts and handwritten attributions</li>
        </ul>
        <p style={{ margin: '16px 0 0', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', lineHeight: 1.6, color: '#888', fontStyle: 'italic' }}>
          These questions are documented to distinguish established fact from areas requiring further research. 
          The archive does not present qualified conclusions as verified facts.
        </p>
      </div>
    </div>
  </section>
);

/* ============================================================================
   SECTION: ABOUT
   ============================================================================ */
const AboutSection = () => (
  <section id="about" style={{ padding: '100px 32px', background: 'white' }} data-testid="about-section">
    <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
      <p style={{ marginBottom: '14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', fontWeight: 700, 
                  letterSpacing: '0.18em', textTransform: 'uppercase', color: '#8B7355' }}>
        About
      </p>
      <h2 style={{ margin: '0 0 32px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px, 4.5vw, 42px)', 
                   fontWeight: 400, color: '#1a1a1a' }}>
        About the Archive
      </h2>

      <div style={{ fontFamily: "'Source Serif 4', serif", fontSize: '17px', lineHeight: 1.85, color: '#444', textAlign: 'left' }}>
        <p style={{ marginBottom: '20px' }}>
          The Elvis Aloha Archive exists to preserve and document materials from the January 1973 Aloha from Hawaii 
          benefit concert. The archive maintains these materials together, preserving the relationships between 
          production documents, proofs, and finished tickets that would be lost if scattered.
        </p>
        <p style={{ marginBottom: '20px' }}>
          The archive is committed to accurate historical presentation. Claims are supported by evidence, uncertain 
          findings are appropriately qualified, and open questions are acknowledged rather than filled with speculation.
        </p>
        <p>
          The collection distinguishes between different categories of material—PSA-certified proofs, admission 
          tickets, used stubs, production mockups, and supporting documentation—rather than combining them under 
          misleading generalizations.
        </p>
      </div>

      {/* Contact */}
      <div style={{ marginTop: '48px', padding: '32px', background: '#fafaf8', border: '1px solid #e8e6e2' }}>
        <h3 style={{ margin: '0 0 16px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '15px', fontWeight: 700, color: '#1a1a1a' }}>
          Inquiries
        </h3>
        <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '15px', lineHeight: 1.7, color: '#666' }}>
          Researchers, institutions, and serious inquiries may contact the archive for additional information, 
          access to documentation, or scholarly consultation.
        </p>
      </div>
    </div>
  </section>
);

/* ============================================================================
   FOOTER
   ============================================================================ */
const Footer = () => (
  <footer style={{ padding: '64px 32px 40px', background: '#1a1a1a', color: 'white' }}>
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '48px', marginBottom: '40px' }}>
        <div style={{ maxWidth: '340px' }}>
          <p style={{ margin: '0 0 14px', fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', fontWeight: 600, letterSpacing: '0.06em' }}>
            ELVIS ALOHA ARCHIVE
          </p>
          <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', lineHeight: 1.7, color: 'rgba(255,255,255,0.6)' }}>
            Preserving documented materials from Elvis Presley's January 1973 Aloha from Hawaii benefit concert.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '48px', flexWrap: 'wrap' }}>
          <div>
            <p style={{ margin: '0 0 14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 700, 
                        letterSpacing: '0.12em', textTransform: 'uppercase', color: '#C9A962' }}>
              Sections
            </p>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {['Inventory', 'PSA Proofs', 'Production', 'Graceland', 'Provenance', 'About'].map(item => (
                <button key={item} onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-'))}
                  style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', 
                           fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}>
                  {item}
                </button>
              ))}
            </nav>
          </div>
          <div>
            <p style={{ margin: '0 0 14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 700, 
                        letterSpacing: '0.12em', textTransform: 'uppercase', color: '#C9A962' }}>
              Legal
            </p>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <a href="/rights" style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Rights</a>
              <a href="/privacy" style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Privacy</a>
              <a href="/accessibility" style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Accessibility</a>
            </nav>
          </div>
        </div>
      </div>

      <div style={{ paddingTop: '28px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <p style={{ margin: '0 0 14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', lineHeight: 1.7, color: 'rgba(255,255,255,0.4)' }}>
          <strong style={{ color: 'rgba(255,255,255,0.6)' }}>Independent Archive:</strong> Elvis Aloha Archive 
          is an independent historical archive. Not affiliated with Elvis Presley Enterprises, Graceland, PSA, 
          or any official Elvis Presley organization. Names and trademarks remain the property of their respective rights holders.
        </p>
        <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', color: 'rgba(255,255,255,0.3)' }}>
          © {new Date().getFullYear()} Elvis Aloha Archive
        </p>
      </div>
    </div>
  </footer>
);

/* ============================================================================
   MAIN PAGE
   ============================================================================ */
const ArtifactShowcasePage = () => {
  return (
    <div data-testid="artifact-showcase-page" style={{ background: 'white' }}>
      <Navigation />
      <HeroSection />
      <InventorySection />
      <PSAProofsSection />
      <ProductionSection />
      <GracelandSection />
      <HistoricalSection />
      <ProvenanceSection />
      <AboutSection />
      <Footer />
    </div>
  );
};

export default ArtifactShowcasePage;
